import type { FC, ReactNode } from 'react'
import type { ProjectSummary } from '@/types'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { normalizeHex } from '@/lib/color'
import { SmartImage } from '@/components/generals/smart-image'

interface ProjectCardProps {
    project: ProjectSummary
    index: number
}

export const ProjectCard: FC<ProjectCardProps> = ({ project, index }): ReactNode => {
    const accent = normalizeHex(project.accent)

    return (
        <Link href={`/work/${project.slug}`} data-cursor='view' className='group block rounded-2xl'>
            <div className='relative aspect-[4/5] overflow-hidden rounded-2xl bg-muted'>
                {project.cover_image ? (
                    <SmartImage
                        src={project.cover_image}
                        alt={project.cover_image_alt ?? ''}
                        sizes='(min-width: 768px) 46vw, 100vw'
                        className='object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]'
                    />
                ) : (
                    <div aria-hidden='true' className='absolute inset-0' style={{ backgroundColor: accent }} />
                )}

                <span className='absolute left-4 top-4 rounded-full bg-background/90 px-3 py-1.5 font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-foreground backdrop-blur md:left-5 md:top-5'>
                    {String(index + 1).padStart(2, '0')}
                </span>

                <span
                    aria-hidden='true'
                    className='absolute bottom-4 right-4 grid size-12 place-items-center rounded-full bg-background text-foreground shadow-lg transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:rotate-45 md:bottom-5 md:right-5 md:size-14'
                >
                    <ArrowUpRight className='size-5' />
                </span>
            </div>

            <div className='mt-5'>
                <div className='flex items-center justify-between gap-4 font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground'>
                    <span className='inline-flex min-w-0 items-center gap-2'>
                        <span aria-hidden='true' className='size-2 shrink-0 rounded-full' style={{ backgroundColor: accent }} />

                        {project.category && (
                            <span data-mdlfy-cms={`projects:${project.id}:category`} className='truncate'>
                                {project.category}
                            </span>
                        )}
                    </span>

                    {project.year && (
                        <span data-mdlfy-cms={`projects:${project.id}:year`} className='shrink-0'>
                            {project.year}
                        </span>
                    )}
                </div>

                <h3 className='mt-3 font-heading text-[1.75rem] font-semibold leading-[1.05] tracking-[-0.035em] md:text-[2.5rem]'>
                    <span
                        data-mdlfy-cms={`projects:${project.id}:name`}
                        className='bg-linear-to-r from-current to-current bg-[length:0%_2px] bg-left-bottom bg-no-repeat box-decoration-clone transition-[background-size] duration-500 ease-out group-hover:bg-[length:100%_2px]'
                    >
                        {project.name}
                    </span>
                </h3>

                {project.summary && (
                    <p data-mdlfy-cms={`projects:${project.id}:summary`} className='mt-3 line-clamp-2 max-w-lg text-[17px] leading-relaxed text-muted-foreground'>
                        {project.summary}
                    </p>
                )}
            </div>
        </Link>
    )
}
